import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { trafficLogsApi, type TrafficLogHost } from '../api/client'
import { formatBytes } from '../hooks/useBackups'
import { useListState } from '../hooks/useListState'
import { sortRows } from '../lib/list-state'
import { Badge, Card, CardHeader, EmptyState, Input, PageHeader, Skeleton, SortableTh } from '../components/ui'

// 5651 trafik logu arşivi — salt-okunur metadata paneli. Şifre çözme bilerek
// yok: AES anahtarı API'ye mount edilmez, çıkarma sunucudaki extract-5651.sh
// ile yapılır (bkz. internal-docs/5651-RUNBOOK.md).

function issueText(issue: string, t: (k: string, o?: Record<string, unknown>) => string): string {
  const [kind, value] = issue.split(':', 2)
  switch (kind) {
    case 'missing_day':
      return t('trafficLogs.issueMissingDay', { date: value })
    case 'missing_manifest':
      return t('trafficLogs.issueMissingManifest', { tag: value })
    case 'missing_stamp':
      return t('trafficLogs.issueMissingStamp', { tag: value })
    default:
      return issue
  }
}

export default function TrafficLogsPage() {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'traffic-logs'],
    queryFn: trafficLogsApi.overview,
    refetchInterval: 60_000,
  })

  const list = useListState<'host' | 'files' | 'bytes' | 'first_date' | 'last_date'>({
    sortKey: 'bytes',
    defaultDirFor: (key) => (key === 'host' ? 'asc' : 'desc'),
  })
  const hosts = useMemo(() => {
    const all = data?.hosts ?? []
    const q = list.debouncedSearch.trim().toLowerCase()
    const filtered = q ? all.filter((h) => h.host.toLowerCase().includes(q)) : all
    return sortRows(filtered, list.sortKey as keyof TrafficLogHost, list.sortDir)
  }, [data?.hosts, list.debouncedSearch, list.sortKey, list.sortDir])

  const runs = data?.runs ?? []
  const issues = data?.issues ?? []

  if (isLoading) {
    return (
      <div className="p-4 sm:p-8 space-y-3">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="p-4 sm:p-8">
        <p className="text-sm text-red-600">{t('trafficLogs.loadFailed')}</p>
      </div>
    )
  }

  if (!data?.configured) {
    return (
      <div className="p-4 sm:p-8">
        <PageHeader title={t('trafficLogs.title')} description={t('trafficLogs.subtitle')} />
        <EmptyState title={t('trafficLogs.notConfiguredTitle')} description={t('trafficLogs.notConfiguredDesc')} />
      </div>
    )
  }

  const lastRun = runs[0]

  return (
    <div className="p-4 sm:p-8 space-y-6">
      <PageHeader title={t('trafficLogs.title')} description={t('trafficLogs.subtitle')} className="mb-0" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="text-xs text-gray-500">{t('trafficLogs.statRuns')}</div>
          <div className="text-xl font-bold text-gray-900">{runs.length}</div>
        </Card>
        <Card>
          <div className="text-xs text-gray-500">{t('trafficLogs.statHosts')}</div>
          <div className="text-xl font-bold text-gray-900">{data.hosts?.length ?? 0}</div>
        </Card>
        <Card>
          <div className="text-xs text-gray-500">{t('trafficLogs.statSize')}</div>
          <div className="text-xl font-bold text-gray-900">{formatBytes(data.total_bytes ?? 0)}</div>
        </Card>
        <Card>
          <div className="text-xs text-gray-500">{t('trafficLogs.statLastRun')}</div>
          <div className="text-xl font-bold text-gray-900">{lastRun ? lastRun.date : '-'}</div>
          {lastRun && (
            <Badge tone={lastRun.stamped ? 'success' : 'warning'}>
              {lastRun.stamped ? t('trafficLogs.stamped') : t('trafficLogs.notStamped')}
            </Badge>
          )}
        </Card>
      </div>

      {issues.length > 0 && (
        <Card tone="warning">
          <CardHeader title={t('trafficLogs.issuesTitle')} />
          <ul className="text-sm text-amber-800 list-disc pl-5 space-y-1">
            {issues.map((i) => (
              <li key={i}>{issueText(i, t)}</li>
            ))}
          </ul>
        </Card>
      )}

      <Card padded={false}>
        <div className="p-5 pb-3 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <CardHeader title={t('trafficLogs.hostsTitle')} className="mb-0" />
          <Input
            type="search"
            value={list.search}
            onChange={(e) => list.setSearch(e.target.value)}
            placeholder={t('trafficLogs.searchPlaceholder')}
            className="sm:w-64"
          />
        </div>
        {hosts.length === 0 ? (
          <div className="p-5 pt-0">
            <EmptyState title={t('trafficLogs.emptyHosts')} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                  <SortableTh label={t('trafficLogs.colHost')} sortKey="host" current={list.sortKey} dir={list.sortDir} onSort={list.toggleSort} className="px-4 py-2" />
                  <SortableTh label={t('trafficLogs.colFiles')} sortKey="files" current={list.sortKey} dir={list.sortDir} onSort={list.toggleSort} className="px-4 py-2" />
                  <SortableTh label={t('trafficLogs.colSize')} sortKey="bytes" current={list.sortKey} dir={list.sortDir} onSort={list.toggleSort} className="px-4 py-2" />
                  <SortableTh label={t('trafficLogs.colFirst')} sortKey="first_date" current={list.sortKey} dir={list.sortDir} onSort={list.toggleSort} className="px-4 py-2" />
                  <SortableTh label={t('trafficLogs.colLast')} sortKey="last_date" current={list.sortKey} dir={list.sortDir} onSort={list.toggleSort} className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {hosts.map((h) => (
                  <tr key={h.host} className="border-b border-gray-100 last:border-0">
                    <td className="px-4 py-2 font-medium text-gray-900">{h.host}</td>
                    <td className="px-4 py-2 text-gray-600">{h.files}</td>
                    <td className="px-4 py-2 text-gray-600">{formatBytes(h.bytes)}</td>
                    <td className="px-4 py-2 text-gray-600">{h.first_date}</td>
                    <td className="px-4 py-2 text-gray-600">{h.last_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card padded={false}>
        <div className="p-5 pb-3">
          <CardHeader title={t('trafficLogs.runsTitle')} description={t('trafficLogs.runsHint')} className="mb-0" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                <th className="px-4 py-2 font-medium">{t('trafficLogs.colDate')}</th>
                <th className="px-4 py-2 font-medium">{t('trafficLogs.colFiles')}</th>
                <th className="px-4 py-2 font-medium">{t('trafficLogs.colSize')}</th>
                <th className="px-4 py-2 font-medium">{t('trafficLogs.colIntegrity')}</th>
              </tr>
            </thead>
            <tbody>
              {runs.slice(0, 30).map((run) => (
                <tr key={run.tag} className="border-b border-gray-100 last:border-0">
                  <td className="px-4 py-2 font-medium text-gray-900">{run.date}</td>
                  <td className="px-4 py-2 text-gray-600">{run.files}</td>
                  <td className="px-4 py-2 text-gray-600">{formatBytes(run.bytes)}</td>
                  <td className="px-4 py-2">
                    <Badge tone={run.manifest && run.stamped ? 'success' : 'danger'}>
                      {run.manifest && run.stamped ? t('trafficLogs.stamped') : t('trafficLogs.notStamped')}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-xs text-gray-500">{t('trafficLogs.extractionNote')}</p>
    </div>
  )
}
